import { useContext, useEffect, useRef, useState } from 'react';

import { fetchPostSellerPayLink } from '@iwoca/lapi-client/iwocapay';
import { AlertBox, Button } from '@iwoca/orion';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';

import styles from './OneTimeLinksPage.module.css';
import { ActivationContext } from '../../../activation/Activation';
import {
  TPricingTypes,
  TPricingType,
} from '../../../Buyer/PayLinkLanding/utils/PayLinkLanding.types';
import { Input as IWpay_Input } from '../../../components/Input/Input';
import { LockedButton } from '../../../components/LockedButton/LockedButton';
import { PayMeLink } from '../../../components/PayMeLink/PayMeLink';
import { useSellerHandle } from '../../../hooks/useSellerHandle.hook';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { createToast } from '../../../store/IwToast';
import { getPayLinkUrl } from '../../../utils/getPayLinkUrl';
import {
  PayLinkInputs,
  PricingTermOption,
} from '../../ManageAccount/components/PayLaterPricingSettings/PayLaterPricingSettings';
import { CheckoutPreview } from '../components/CheckoutPreview/CheckoutPreview';
import { PayLinkPricingFormField } from '../components/PayLinkPricingFormField/PayLinkPricingFormField';
import {
  Column,
  VerticalSplitLayout,
} from '../components/VerticalSplitLayout/VerticalSplitLayout';
import { usePayIn12 } from '../hooks/usePayIn12';
import payLinksPageStyles from '../PayLinks.module.css';
import { DEFAULT_12M_PRICING_OPTION } from '../PayLinksNavigation';
import { usePreview } from '../usePreview';
import { validateForm } from '../utils/validateForm';

export const OneTimeLinksPage = () => {
  const { sellerHandle } = useSellerHandle();
  const { previewValues, setPreviewValuesFromEvent, setPreviewValues } =
    usePreview();
  const isFeatureFlag = () => {
    return localStorage.relocatedPricing === 'true';
  };

  if (!sellerHandle) return null;

  return (
    <VerticalSplitLayout className={payLinksPageStyles.PayLinksPage}>
      <Column>
        {isFeatureFlag() ? (
          <NewLeftSide
            previewValues={previewValues}
            setPreviewValues={setPreviewValues}
          />
        ) : (
          <OldLeftSide
            previewValues={previewValues}
            setPreviewValuesFromEvent={setPreviewValuesFromEvent}
          />
        )}
      </Column>
      <Column>
        {!!previewValues.pricing_3m ? (
          <CheckoutPreview
            sellerName={previewValues.sellerName}
            amount={previewValues.amount}
            reference={previewValues.reference}
            pricingPromotions={{
              THREE_MONTHS: previewValues.pricing_3m.promotions,
              TWELVE_MONTHS: previewValues.pricing_12m?.promotions,
            }}
            representativeInterest={{
              THREE_MONTHS: previewValues.pricing_3m.representativeInterest,
              TWELVE_MONTHS: previewValues.pricing_12m?.representativeInterest,
            }}
          />
        ) : (
          <div>Loading preview...</div>
        )}
      </Column>
    </VerticalSplitLayout>
  );
};

const OldLeftSide = ({
  previewValues,
  setPreviewValuesFromEvent,
}: {
  previewValues: ReturnType<typeof usePreview>['previewValues'];
  setPreviewValuesFromEvent: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}) => {
  return (
    <div className={payLinksPageStyles.leftSide}>
      <h1 className={payLinksPageStyles.title}>
        A custom pay link for a specific invoice
      </h1>
      <p className={payLinksPageStyles.description}>
        You set the amount, reference and terms tailored to your customer <br />
        - <b>it can only be used once.</b>
      </p>

      {previewValues?.pricing_3m ? (
        <CreateOneTimePayLinkForm
          onChange={setPreviewValuesFromEvent}
          pricingTypes={{
            pricing_3m: previewValues.pricing_3m?.type as TPricingType,
            pricing_12m: previewValues.pricing_12m?.type as TPricingType,
          }}
        />
      ) : null}
    </div>
  );
};

const NewLeftSide = ({
  previewValues,
  setPreviewValues,
}: {
  previewValues: ReturnType<typeof usePreview>['previewValues'];
  setPreviewValues: (name: string, value: unknown) => void;
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [payLink, setPayLink] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: string }>({});

  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [threeMonthPricing, setThreeMonthPricing] = useState<TPricingType>();
  const [twelveMonthPricing, setTwelveMonthPricing] = useState<TPricingType>();

  useEffect(() => {
    setThreeMonthPricing(previewValues.pricing_3m?.type as TPricingType);
    setTwelveMonthPricing(
      (previewValues.pricing_12m?.type as TPricingType) ||
        DEFAULT_12M_PRICING_OPTION,
    );
  }, [previewValues.pricing_3m, previewValues.pricing_12m]);

  const { stateKey } = useStateKey();
  const { isOnboarded } = useContext(ActivationContext);

  const formSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError('Please provide a pay link amount')
      .min(0, 'Please provide a pay link amount')
      .required(),
    reference: Yup.string()
      .max(50, 'Limit 50 characters reached')
      .required('Please provide a pay link reference'),
    pricing_3m: Yup.string()
      .matches(/(SELLER_FREE_BUYER_PAYS|SELLER_PAYS_BUYER_FREE)/)
      .required(),
    pricing_12m: Yup.string().matches(
      /(SELLER_FREE_BUYER_PAYS|SELLER_PAYS_BUYER_FREE)/,
    ),
  });

  const submit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const data = await validateForm(ev.currentTarget, formSchema, setErrors);

    if (data) {
      const body = {
        reference: data.reference,
        amount: data.amount,
        product_pricing: {
          THREE_MONTHS: data.pricing_3m as TPricingType,
          ...(data.pricing_12m && {
            TWELVE_MONTHS: data.pricing_12m as TPricingType,
          }),
        },
      };

      try {
        const { seller_name, id } = await fetchPostSellerPayLink({
          stateKey: stateKey!,
          body: body,
        });

        const payLinkUrl = getPayLinkUrl(seller_name!, id!);
        setPayLink(payLinkUrl);
      } catch (e) {
        createToast(ERROR_MESSAGE);
      }
    }
  };

  return (
    <form
      ref={formRef}
      aria-label="customPayLinkForm"
      className="mt-2xl grid grid-cols-1 gap-xl"
      onSubmit={submit}
    >
      <div>
        <h1 className="mb-[0] mt-[0] text-2xl font-bold text-primary-10">
          A one-time custom pay link
        </h1>
        <p className="mb-[0] mt-s text-m font-reg text-primary-10">
          To be used once for a specific payment.
        </p>
      </div>

      <div>
        <h2 className="mb-[0] mt-[0] text-l font-bold text-primary-10">
          Choose who pays for this Pay Layer via this pay link
        </h2>
        <p className="mb-[0] mt-s text-m font-reg text-primary-10">
          These settings will apply only to this pay link.
        </p>
      </div>

      <PayLinkInputs
        amount={amount}
        setAmount={setAmount}
        reference={reference}
        setReference={setReference}
        setPreviewValues={setPreviewValues}
        validateForm={() =>
          validateForm(formRef.current!, formSchema, setErrors)
        }
        setTouched={setTouched}
        errors={errors}
        touched={touched}
      />

      <PricingTermOption
        productKey={'THREE_MONTHS'}
        pricingType={threeMonthPricing}
        onChange={(pricingType) => {
          setThreeMonthPricing(pricingType);
          setPreviewValues('pricing_3m', pricingType);
        }}
      />

      <PricingTermOption
        productKey={'TWELVE_MONTHS'}
        pricingType={twelveMonthPricing}
        onChange={(pricingType) => {
          setTwelveMonthPricing(pricingType);
          setPreviewValues('pricing_12m', pricingType);
        }}
      />

      <div className="flex flex-col gap-m">
        {payLink && <PayMeLink payMeUrl={payLink} />}
        {isOnboarded ? (
          <Button
            disabled={
              Object.keys(errors).length > 0 ||
              Object.keys(touched).length !== 2
            }
            className="w-fit"
          >
            Generate one-time custom link
          </Button>
        ) : (
          <LockedButton />
        )}
      </div>
    </form>
  );
};

const ERROR_MESSAGE =
  'Error creating pay link. Please refresh the page and try again.';

const CreateOneTimePayLinkForm = ({
  onChange,
  pricingTypes,
}: {
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  pricingTypes: TPricingTypes;
}) => {
  const [payLink, setPayLink] = useState<string | null>(null);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const { stateKey } = useStateKey();
  const { payIn12Enabled } = usePayIn12();
  const INITIAL_VALUES = {
    amount: '',
    reference: '',
    pricing_3m: pricingTypes.pricing_3m,
    pricing_12m: pricingTypes.pricing_12m || DEFAULT_12M_PRICING_OPTION,
  };

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={Yup.object().shape({
        amount: Yup.number().required('Please provide a pay link amount'),
        reference: Yup.string().required('Please provide a pay link reference'),
        pricing_3m: Yup.string().required('Please provide a pay link pricing'),
      })}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        setHasSubmitError(false);
        try {
          const { seller_name, id } = await fetchPostSellerPayLink({
            stateKey: stateKey!,
            body: {
              reference: values.reference,
              amount: parseFloat(values.amount),
              product_pricing: {
                THREE_MONTHS: values.pricing_3m,
                ...(payIn12Enabled && {
                  TWELVE_MONTHS: values.pricing_12m,
                }),
              },
            },
          });

          const payLinkUrl = getPayLinkUrl(seller_name!, id!);
          setPayLink(payLinkUrl);
        } catch (e) {
          setHasSubmitError(true);
        }
        actions.setSubmitting(false);
      }}
      onReset={() => {
        setPayLink(null);
      }}
    >
      {({
        resetForm,
        values,
        handleChange,
      }: FormikProps<typeof INITIAL_VALUES>) => {
        return (
          <Form>
            {hasSubmitError ? (
              <AlertBox variant="failure" className={styles.submitError}>
                {ERROR_MESSAGE}
              </AlertBox>
            ) : null}
            <div className={styles.payLinkConfiguration}>
              <IWpay_Input
                label="Order Amount"
                name="amount"
                type="number"
                onChange={onChange}
                placeholder="Amount"
                className={styles.configurationInput}
              />
              <IWpay_Input
                label="Invoice Reference"
                name="reference"
                onChange={onChange}
                placeholder="Reference"
                className={styles.configurationInput}
              />
            </div>
            <PayLinkPricingFormField
              productPricing={{
                pricing_3m: values.pricing_3m,
                pricing_12m: values.pricing_12m,
              }}
              onChange={(e) => {
                onChange(e);
                handleChange(e);
              }}
            />
            {payLink === null ? (
              <div className={payLinksPageStyles.buttonWrapper}>
                <OneTimeLinkButton />
              </div>
            ) : (
              <div className={payLinksPageStyles.payLinkContainer}>
                <PayMeLink payMeUrl={payLink} />
                <div
                  className={payLinksPageStyles.createNewLinkButtonContainer}
                >
                  <Button
                    variant="primary"
                    className={styles.linkButton}
                    onClick={() => resetForm()}
                  >
                    Create a new one-time link
                  </Button>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

function OneTimeLinkButton() {
  const { isOnboarded, isLoading } = useContext(ActivationContext);

  if (isLoading) return null;

  return (
    <>
      {isOnboarded ? (
        <Button variant="primary" type="submit" className={styles.linkButton}>
          Send a one-time pay link
        </Button>
      ) : (
        <LockedButton />
      )}
    </>
  );
}
